import { 
  getAuth, 
  createUserWithEmailAndPassword, 
  signInWithEmailAndPassword, 
  signOut, 
  updateProfile,
  updateEmail,
  updatePassword,
  sendPasswordResetEmail,
  onAuthStateChanged
} from 'firebase/auth';
import { doc, setDoc, getDoc } from 'firebase/firestore';
import { auth, db } from './config';

// Authentication functions
export const registerUser = async (email, password, displayName) => {
  try {
    // Validate inputs
    if (!email || !password || !displayName) {
      throw new Error('All fields are required: email, password, and name');
    }
    
    // Validate email format
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      throw new Error('Invalid email format');
    }
    
    // Validate password length
    if (password.length < 6) {
      throw new Error('Password must be at least 6 characters');
    }
    
    const userCredential = await createUserWithEmailAndPassword(auth, email, password);
    
    const defaultUserPhoto = '/images/default-user.png';
    
    // Update user profile with displayName and default photo
    await updateProfile(userCredential.user, {
      displayName: displayName,
      photoURL: defaultUserPhoto
    });
    
    // Create user document in Firestore
    await setDoc(doc(db, 'users', userCredential.user.uid), {
      displayName: displayName,
      email: email,
      createdAt: new Date(),
      isAdmin: false,
      columnTitle: displayName + "'s Column", // default
      photoURL: defaultUserPhoto,
      bio: null
    });
    
    return userCredential.user;
  } catch (error) {
    // Handle specific registration errors
    if (error.code === 'auth/email-already-in-use') {
      throw new Error('This email is already in use');
    } else if (error.code === 'auth/invalid-email') {
      throw new Error('Invalid email format');
    } else if (error.code === 'auth/weak-password') {
      throw new Error('Password is too weak');
    } else if (error.message) {
      throw new Error(error.message);
    } else {
      throw new Error('Registration error, please try again');
    }
  }
};

export const loginUser = async (email, password) => {
  try {
    // Validate inputs
    if (!email || !password) {
      throw new Error('Email and password are required');
    }
    
    const userCredential = await signInWithEmailAndPassword(auth, email, password);
    return userCredential.user;
  } catch (error) {
    // Handle specific login errors
    if (error.code === 'auth/user-not-found' || error.code === 'auth/wrong-password' || error.code === 'auth/invalid-credential') {
      throw new Error('Invalid email or password');
    } else if (error.code === 'auth/user-disabled') {
      throw new Error('This account has been disabled');
    } else if (error.message) {
      throw new Error(error.message);
    } else {
      throw new Error('Login error, please try again');
    }
  }
};

export const logoutUser = async () => {
  try {
    await signOut(auth);
    return true;
  } catch (error) {
    console.error('Logout error:', error);
    throw new Error('Error during logout');
  }
};

export const updateUserProfile = async (userData) => {
  try {
    const user = auth.currentUser;
    if (!user) throw new Error('No logged in user');
    
    const { displayName, photoURL, columnTitle, bio } = userData;
    const defaultUserPhoto = '/images/default-user.png';
    
    // Update Firebase Auth
    const authUpdates = {};
    if (displayName) authUpdates.displayName = displayName;
    if (photoURL !== undefined) {
      authUpdates.photoURL = photoURL || defaultUserPhoto;
    }
    
    if (Object.keys(authUpdates).length > 0) {
      await updateProfile(user, authUpdates);
    }
    
    // Update Firestore
    const userDocRef = doc(db, 'users', user.uid);
    const userDoc = await getDoc(userDocRef);
    
    if (userDoc.exists()) {
      const firestoreUpdates = {};
      if (displayName) firestoreUpdates.displayName = displayName;
      if (photoURL !== undefined) {
        firestoreUpdates.photoURL = photoURL || defaultUserPhoto;
      }
      if (columnTitle) firestoreUpdates.columnTitle = columnTitle;
      if (bio !== undefined) firestoreUpdates.bio = bio;
      
      if (Object.keys(firestoreUpdates).length > 0) {
        await setDoc(userDocRef, firestoreUpdates, { merge: true });
      }
    } else {
      // Create user document if it doesn't exist
      await setDoc(userDocRef, {
        displayName: displayName || user.displayName || 'User',
        email: user.email,
        createdAt: new Date(),
        photoURL: photoURL || defaultUserPhoto,
        columnTitle: columnTitle || displayName + "'s Column",
        bio: bio || null,
        isAdmin: false
      });
    }
    
    return user;
  } catch (error) {
    console.error('Profile update error:', error);
    throw new Error('Error updating profile');
  }
};

export const updateUserEmail = async (newEmail) => {
  try {
    const user = auth.currentUser;
    if (!user) throw new Error('No logged in user');
    
    // Validate email format
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(newEmail)) {
      throw new Error('Invalid email format');
    }
    
    await updateEmail(user, newEmail);
    
    // Update email in Firestore
    const userDocRef = doc(db, 'users', user.uid);
    await setDoc(userDocRef, { email: newEmail }, { merge: true });
    
    return user;
  } catch (error) {
    if (error.code === 'auth/requires-recent-login') {
      throw new Error('Please login again and retry');
    } else if (error.code === 'auth/email-already-in-use') {
      throw new Error('This email is already in use');
    } else {
      throw new Error('Error updating email');
    }
  }
};

export const updateUserPassword = async (newPassword) => {
  try {
    const user = auth.currentUser;
    if (!user) throw new Error('No logged in user');
    
    // Validate password length
    if (newPassword.length < 6) {
      throw new Error('Password must be at least 6 characters');
    }
    
    await updatePassword(user, newPassword);
    return true;
  } catch (error) {
    if (error.code === 'auth/requires-recent-login') {
      throw new Error('Please login again and retry');
    } else if (error.code === 'auth/weak-password') {
      throw new Error('Password is too weak');
    } else {
      throw new Error('Error updating password');
    }
  }
};

export const resetPassword = async (email) => {
  try {
    if (!email) {
      throw new Error('Email is required');
    }
    
    await sendPasswordResetEmail(auth, email);
    return true;
  } catch (error) {
    if (error.code === 'auth/user-not-found') {
      throw new Error('No user found with this email');
    } else {
      throw new Error('Error sending password reset email');
    }
  }
};

export const getCurrentUser = () => {
  return auth.currentUser;
};

export const onAuthChange = (callback) => {
  return onAuthStateChanged(auth, callback);
};
